import React, { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useNavigate, useLocation } from "react-router-dom";
import logout from "./Logout"; // Import the plain logout function
import Divider from "@mui/material/Divider";

interface LayoutProps {
  children: React.ReactNode;
  isLoggedIn: boolean;
  onLogout: () => void;
  userName?: string | null;
}

const Layout: React.FC<LayoutProps> = ({ children, isLoggedIn, onLogout, userName }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to /home if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/home"); // Redirect unauthorized users to /home
    }
  }, [isLoggedIn, navigate]);

  const renderDrawer = () => (
    <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
      <List>
        {/* Home navigation */}
        <ListItem
          component="button"
          onClick={() => {
            setDrawerOpen(false);
            navigate("/home");
          }}
        >
          <ListItemText primary="Home" />
        </ListItem>
        {/* Main GIS Data navigation */}
        {/* <ListItem
          component="button"
          onClick={() => {
            setDrawerOpen(false);
            navigate("/upload");
          }}
        >
          <ListItemText primary="Upload Primary Pipe GIS Data" />
        </ListItem> */}
        {/* COF Analysis navigation */}
        <ListItem
          component="button"
          onClick={() => {
            setDrawerOpen(false);
            navigate("/form");
          }}
        >
          <ListItemText primary="Upload Files and Run COF Analysis" />
        </ListItem>
        {/* Corrosive Scoring navigation */}
        <ListItem
          component="button"
          onClick={() => {
            setDrawerOpen(false);
            navigate("/concreteSteel");
          }}
        >
          <ListItemText primary="Process Soil Corrosivity Data" />
        </ListItem>
        {/* Hydraulic Analysis navigation */}
        <ListItem
          component="button"
          onClick={() => {
            setDrawerOpen(false);
            navigate("/uploadHydro");
          }}
        >
          <ListItemText primary="Process Water System Hydraulic Data" />
        </ListItem>
      </List>
    </Drawer>
  );

  return (
    <div style={{ width: "100%", minHeight: "100vh" }}>
      <AppBar position="static">
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
            px: { xs: 1, md: 2 },
          }}
        >
          {isLoggedIn && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ flexShrink: 0 }}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
          {/* Top Banner Navigation */}
          {isLoggedIn && (
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, ml: 1, flex: "1 1 auto", minWidth: 0 }}>
              <Button color="inherit" size="small" onClick={() => navigate("/displayData")}>
                View Uploaded Data
              </Button>
              <Button color="inherit" size="small" onClick={() => navigate("/form")}>
                COF Analysis
              </Button>
              <Button color="inherit" size="small" onClick={() => navigate("/concreteSteel")}>
                Soil Corrosivity
              </Button>
              <Button color="inherit" size="small" onClick={() => navigate("/uploadHydro")}>
                Hydraulic Data
              </Button>
            </Box>
          )}
          {/* Only show welcome message on home page */}
          {isLoggedIn && (
            <Typography
              variant="body1"
              component="div"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                fontSize: { xs: "0.82rem", md: "0.95rem" },
                minWidth: 0,
                flexShrink: 1,
                ml: { xs: "auto", md: 0 },
              }}
            >
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Box component="span" sx={{ maxWidth: { xs: 120, md: 220 }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {location.pathname === "/home"
                  ? `Welcome, ${userName || "User"}!`
                  : userName || ""}
              </Box>
              {(location.pathname === "/form" || location.pathname === "/concreteSteel") && (
                <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15 }}>
                    <Box sx={{ fontWeight: 700, color: "black", textDecoration: "underline", fontSize: "0.85rem" }}>
                      Base Dataset
                    </Box>
                    <Box sx={{ fontWeight: 400, fontSize: "0.82rem", maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {localStorage.getItem("baseDatasetName") || "No dataset selected"}
                    </Box>
                  </Box>
                </Box>
              )}
            </Typography>
          )}
          {/* logout logic*/}
          {isLoggedIn && (
            <Divider orientation="vertical" flexItem sx={{ mx: { xs: 0.75, md: 1.5 } }} />
          )}
          {isLoggedIn && (
            <Button
              color="inherit"
              size="small"
              sx={{ flexShrink: 0 }}
              onClick={async () => {
                await logout(navigate); // Pass navigate to the logout function
                onLogout(); // Call the parent-provided onLogout callback
              }}
            >
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {renderDrawer()}

      <Box
        sx={{
          padding: 3,
          paddingBottom: 8, // Add extra bottom padding to avoid footer overlap
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 64px)", // Adjust for AppBar height
        }}
      >
        {children}
      </Box>
    </div>
  );
};

export default Layout;